import { type ReactElement, useEffect, useState } from 'react';
import { Button, InlineRow, Ink, Stack } from '@vello/web-theme';
import { useT } from '../i18n/provider';

/**
 * The SOS alert of the console (W8-HELP-CENTER, D448; finding A-12). Before D448 an SOS reached the Admin only as a
 * number in the bell, read every 60 s, with no sound, and only when a city was picked. Now:
 *
 * - the safety queue is read every {@link SOS_POLL_MS} (also with the tab in the background, so the alert can ring) over the
 *   city in scope OR every granted city of the country in scope (`sosAlertMarkets`);
 * - a NEW SOS / critical incident rings a short chime through the Web Audio API (no media file, no autoplay of an element)
 *   — on by default, muted from the bell — and, when the operator allowed it, shows a browser notification that opens the
 *   safety queue ("push in the console": the page's own notification, never a third-party push service);
 * - the first read of a session seeds the "seen" set without ringing (see `newSosIncidents`).
 *
 * Preferences are per browser (localStorage), read at ring time; nothing here decides who may read the queue — the
 * grants and the server do.
 */
export const SOS_POLL_MS = 15_000;
const SOUND_KEY = 'vello.admin.sosSound';
const NOTIFY_KEY = 'vello.admin.sosNotify';

const readFlag = (key: string, fallback: boolean): boolean => {
  try {
    const v = window.localStorage.getItem(key);
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
};
const writeFlag = (key: string, value: boolean): void => {
  try {
    window.localStorage.setItem(key, value ? '1' : '0');
  } catch {
    /* a private window without storage keeps the default */
  }
};

export const sosSoundEnabled = (): boolean => readFlag(SOUND_KEY, true);
export const sosNotifyEnabled = (): boolean => readFlag(NOTIFY_KEY, true);

type AudioCtor = new () => { createOscillator(): OscillatorNode; createGain(): GainNode; destination: AudioDestinationNode; currentTime: number; close(): Promise<void> };

/** Three short tones. Silent when the browser has no Web Audio (or blocks it before any user gesture). */
export function playSosChime(): void {
  if (!sosSoundEnabled()) return;
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (Ctor === undefined) return;
  try {
    const ctx = new Ctor();
    for (let i = 0; i < 3; i += 1) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = i % 2 === 0 ? 880 : 660;
      gain.gain.value = 0.15;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const start = ctx.currentTime + i * 0.3;
      osc.start(start);
      osc.stop(start + 0.2);
    }
    window.setTimeout(() => void ctx.close().catch(() => undefined), 1500);
  } catch {
    /* no audio device */
  }
}

/** The browser notification of new SOS alerts (only when the operator allowed it). Clicking it opens the queue. */
export function notifySos(count: number, title: string, body: string, onOpen: () => void): void {
  if (!sosNotifyEnabled()) return;
  const N = (window as unknown as { Notification?: typeof Notification }).Notification;
  if (N === undefined || N.permission !== 'granted') return;
  try {
    const n = new N(title, { body, tag: 'vello-sos', renotify: count > 0 } as NotificationOptions);
    n.onclick = () => {
      window.focus();
      onOpen();
      n.close();
    };
  } catch {
    /* a browser that refuses page notifications */
  }
}

/** The bell's switches: the chime, and the browser notification (asking the browser's permission once). */
export function SosAlertControls(): ReactElement {
  const { t } = useT();
  const [sound, setSound] = useState(sosSoundEnabled);
  const N = typeof window !== 'undefined' ? (window as unknown as { Notification?: typeof Notification }).Notification : undefined;
  const [permission, setPermission] = useState<string>(N?.permission ?? 'unsupported');
  const [notify, setNotify] = useState(sosNotifyEnabled);
  useEffect(() => writeFlag(SOUND_KEY, sound), [sound]);
  useEffect(() => writeFlag(NOTIFY_KEY, notify), [notify]);
  const allow = async (): Promise<void> => {
    if (N === undefined) return;
    try {
      setPermission(await N.requestPermission());
    } catch {
      setPermission(N.permission);
    }
    setNotify(true);
  };
  return (
    <Stack className="vello-menu__note" gap={2}>
      <span>{t('alerts.sos.title')}</span>
      <InlineRow gap={2} align="stretch" wrap>
        <Button variant="ghost" onClick={() => { const next = !sound; writeFlag(SOUND_KEY, next); setSound(next); if (next) playSosChime(); }} aria-pressed={sound}>{t(sound ? 'alerts.sos.soundOn' : 'alerts.sos.soundOff')}</Button>
        {permission === 'granted' ? (
          <Button variant="ghost" onClick={() => setNotify(!notify)} aria-pressed={notify}>{t(notify ? 'alerts.sos.notifyOn' : 'alerts.sos.notifyOff')}</Button>
        ) : permission === 'default' ? (
          <Button variant="ghost" onClick={() => void allow()}>{t('alerts.sos.allowNotify')}</Button>
        ) : (
          <Ink tone="tertiary">{t(permission === 'denied' ? 'alerts.sos.notifyBlocked' : 'alerts.sos.notifyUnsupported')}</Ink>
        )}
      </InlineRow>
    </Stack>
  );
}
